'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DolguIleMemeDiklestirmePage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dolgu-ile-meme-diklestirme-nedir';
  const pageTitle = 'Dolgu ile Meme Dikleştirme Nedir?';
  const pageDescription = 'Dolgu ile meme dikleştirme, meme dokusu altına veya üst kısmına hyaluronik asit bazlı jel enjekte edilerek, memenin formunu yeniden yapılandırma işlemidir.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Dolgu ile Meme Dikleştirme', href: '/tr/blog/dolgu-ile-meme-diklestirme-nedir' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Dolgu ile meme dikleştirme işlemi ve doğal sonuçlar"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-rose-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Ameliyatsız Estetik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Dolgu ile Meme Dikleştirme Nedir?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>8 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Dolgu ile meme dikleştirme, meme dokusu altına veya üst kısmına hyaluronik asit bazlı jel enjekte edilerek, memenin formunu yeniden yapılandırma işlemidir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Amaç:
              </p>

              <div className="bg-rose-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-rose-800 space-y-2">
                  <li>Hafif sarkmaları düzeltmek</li>
                  <li>Dekolte bölgesine dolgunluk kazandırmak</li>
                  <li>Meme başı pozisyonunu yukarı taşımaktır.</li>
                </ul>
                <p className="text-rose-800 mt-3 font-medium">
                  💎 Ameliyatsız, hızlı, doğal sonuçlu bir işlemdir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Hangi Dolgular Kullanılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                En sık tercih edilen ürünler:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li><strong>Hyaluronik Asit Dolguları (HA):</strong> Yüksek viskoziteli ve dokuya uyumlu</li>
                  <li><strong>CaHA (Kalsiyum Hidroksiapatit) Dolguları:</strong> Kolojen üretimini tetikleyici</li>
                  <li><strong>Otolog Yağ Transferi:</strong> Kendi vücut yağının kullanıldığı doğal yöntem</li>
                </ul>
                <p className="text-blue-800 mt-3 font-medium">
                  Dolgu miktarı genellikle her meme için 50–100 ml arasındadır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. İşlem Nasıl Yapılır?
              </h2>
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside text-green-800 space-y-2">
                  <li>Lokal anestezi uygulanır.</li>
                  <li>İnce kanüllerle meme üst kısmına dolgu enjekte edilir.</li>
                  <li>İşlem ortalama 40 dakika sürer.</li>
                  <li>Hasta aynı gün taburcu edilir.</li>
                </ol>
                <p className="text-green-800 mt-3 font-medium">
                  Etki anında fark edilir, ancak nihai görünüm 1–2 hafta içinde oturur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Kimler İçin Uygundur?
              </h2>
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 font-medium mb-3">✅ Uygundur:</p>
                <ul className="list-disc list-inside text-green-800 space-y-1">
                  <li>Hafif sarkma problemi olanlar</li>
                  <li>Emzirme sonrası form kaybı yaşayanlar</li>
                  <li>Ameliyat istemeyen hastalar</li>
                  <li>Asimetri düzeltme gereksinimi olanlar</li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <p className="text-red-800 font-medium mb-3">⚠️ Uygun değildir:</p>
                <ul className="list-disc list-inside text-red-800 space-y-1">
                  <li>Orta veya ileri derecede sarkma (cerrahi gerekir)</li>
                  <li>Gebelik, emzirme dönemleri</li>
                  <li>Meme dokusunda kitle veya enfeksiyon varlığı</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Avantajları
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dolgu ile Dikleştirme
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Klasik Meme Dikleştirme (Mastopeksi)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Uygulama</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Enjeksiyon, ameliyatsız</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cerrahi kesi ve dikiş</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">İyileşme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1–2 gün</td>
                      <td className="px-6 py-4 text-sm text-gray-900">2–4 hafta</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">İz</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yok</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Var</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Doğallık</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yüksek</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kesi izine bağlı</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kalıcılık</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kalıcı</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <p className="text-purple-800 font-medium">
                  💡 Doğal ve geçici olması sayesinde, kişi istediğinde formu güncelleyebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. İşlem Sonrası Dikkat Edilmesi Gerekenler
              </h2>
              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-orange-800 space-y-2">
                  <li>İlk 48 saat sıcak duş ve basınçtan kaçınılmalı</li>
                  <li>1 hafta spor ve masaj yasak</li>
                  <li>2 hafta boyunca yüzüstü yatılmamalı</li>
                  <li>Bol su içmek, ödemin atılmasını hızlandırır</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Kalıcılık ve Tekrar Süresi
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgunun etkisi 12–18 ay sürer. Düzenli aralıklarla yenilendiğinde meme formu uzun süre korunur.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Bazı durumlarda işlem yağ transferiyle kombine edilerek kalıcılık artırılabilir.
              </p>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Dolgu ile meme dikleştirme işlemi ve doğal sonuçlar"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Dolgu ile meme dikleştirme kalıcı mıdır?</h3>
                  <p className="text-gray-700">Hayır, geçici ama uzun süreli bir çözümdür (1–1.5 yıl).</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Dolgu emzirmeye engel midir?</h3>
                  <p className="text-gray-700">Hayır. Meme dokusunun dışına uygulandığı için süt kanallarını etkilemez.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Dolgu eridiğinde meme eski haline döner mi?</h3>
                  <p className="text-gray-700">Evet, dolgu tamamen emilir ve vücut doğal haline geri döner.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. İşlem sonrası ağrı olur mu?</h3>
                  <p className="text-gray-700">Genelde hafif dolgunluk hissi dışında ağrı yaşanmaz.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Dolgu memede kitle oluşturur mu?</h3>
                  <p className="text-gray-700">Hayır. Hyaluronik asit dolgular biyouyumlu ve tamamen çözünür yapıdadır.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Society of Plastic Surgeons (ASPS) – "Hyaluronic Acid Fillers for Breast Lifting"</li>
                <li>TPRECD (Türk Plastik Cerrahi Derneği) – "Meme Dolgusu Uygulama Rehberi"</li>
                <li>Harvard Health Publishing (2023) – "Non-Surgical Breast Rejuvenation"</li>
                <li>ISAPS Global Report 2024 – "Trends in Non-Invasive Breast Lifting"</li>
                <li>Mayo Clinic – "Minimally Invasive Approaches to Breast Contouring"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#memeestetiği</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#dolguilenedikleştirme</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#ameliyatsızestetik</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm">#doğalgörünüm</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-rose-600 to-pink-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Meme Estetiği Hakkında Bilgi Alın</h3>
              <p className="mb-6">Dolgu ile meme dikleştirme ve diğer ameliyatsız meme estetiği yöntemleri hakkında uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Dolgu ile meme dikleştirme hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-rose-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/memeye-dolgu-uygulamasi-dogal-dolgunluk-ve-sekillendirme"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Memeye Dolgu Uygulaması</h4>
              <p className="text-gray-600 text-sm">Yağ transferi ve hyaluronik asit dolgularla ameliyatsız meme büyütme nasıl yapılır?</p>
            </Link>
            <Link
              href="/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgu İşlemleri Yüz Liftingi Sağlar mı?</h4>
              <p className="text-gray-600 text-sm">Dolgu işlemlerinin yüz liftingi etkisi yaratıp yaratmadığı ve nasıl uygulandığı.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
